<template>
  <div>
    <Head title="Vytvořit kalkulaci - Ohřev vody" />
    <div class="flex flex-col gap-2 items-center justify-between mb-8 xl:flex-row">
      <h1 class="text-3xl font-bold">
        Kalkulace OP: <span class="text-red-400">{{ calculation.case_code }}</span> (v.{{ calculation.version }})
      </h1>
      <div class="flex gap-2">
        <div class="p-2 text-gray-500 bg-gray-200 rounded-md"><strong>Vytvořeno:</strong> {{ $filters.date(calculation.created_at) }}</div>
        <div class="p-2 text-gray-500 bg-gray-200 rounded-md"><strong>OZ:</strong> {{ calculation.user.first_name }} {{ calculation.user.last_name }}</div>
        <div class="p-2 text-gray-500 bg-gray-200 rounded-md"><strong>Typ:</strong> {{ calculation.type === 'fve' ? 'Fotovoltaika' : calculation.type === 'heating-water' ? 'Ohřev vody' : '-' }}</div>
      </div>
    </div>
    <div class="bg-white rounded-md shadow overflow-hidden">
      <div class="flex flex-wrap -mb-8 p-8">
        <div class="mt-4 w-full">
          <div class="grid gap-8 grid-cols-2">
            <div class="col-span-2 w-full overflow-x-auto lg:col-span-1">
              <h2 class="text-2xl font-bold">Vstupní data</h2>
              <table class="w-full whitespace-nowrap">
                <thead>
                  <tr class="text-left font-bold">
                    <th class="pb-4 pt-6 text-sm">Klíč</th>
                    <th class="pb-4 pt-6 text-sm">Hodnota</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(value, key) in calculation.data.entryData" :key="key">
                    <tr v-if="key !== 'components'">
                      <td class="py-1 text-xs border-t">{{ key }}</td>
                      <td class="py-1 text-xs border-t">{{ value }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div class="col-span-2 w-full overflow-x-auto lg:col-span-1">
              <h2 class="text-2xl font-bold">Výsledky</h2>
              <table class="w-full whitespace-nowrap">
                <thead>
                  <tr class="text-left font-bold">
                    <th class="pb-4 pt-6 text-sm">Klíč</th>
                    <th class="pb-4 pt-6 text-sm">Hodnota</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(value, key) in calculation.data.calculation" :key="key">
                    <tr v-if="key !== 'components'">
                      <td class="py-1 text-xs border-t">{{ key }}</td>
                      <td v-if="key === 'pdf'" class="py-1 text-xs border-t"><a :href="value.url" class="text-indigo-500 hover:text-indigo-600" target="_blank">Stáhnout PDF (kalkulace)</a></td>
                      <td v-else-if="key === 'pdfOffer'" class="py-1 text-xs border-t"><a :href="value.url" class="text-indigo-500 hover:text-indigo-600" target="_blank">Stáhnout PDF (nabídka)</a></td>
                      <td v-else-if="key === 'pdf2'" class="py-1 text-xs border-t"><a :href="value.url" class="text-indigo-500 hover:text-indigo-600" target="_blank">Stáhnout PDF (příloha č.7)</a></td>
                      <td v-else class="py-1 text-xs border-t">{{ value }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex gap-2 items-center mt-8">
            <h2 class="text-2xl font-bold">Komponenty</h2>
          </div>
          <div class="mb-4 w-full overflow-x-auto">
            <table v-if="calculation.data.entryData.components.length !== 0" class="w-full whitespace-nowrap">
              <thead>
                <tr class="text-left font-bold">
                  <th class="pb-4 pt-6 text-sm">Komponenty</th>
                  <th class="pb-4 pt-6 px-6 text-sm">Počet (ks)</th>
                  <th class="pb-4 pt-6 px-6 text-right text-sm">Cena/ks</th>
                  <th class="pb-4 pt-6 px-6 text-right text-sm">Celkem</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="component in calculation.data.entryData.components" :key="component.id" class="hover:bg-gray-100 focus-within:bg-gray-100" :class="{ 'text-gray-400': component.quantity === 0 }">
                  <td class="py-1 text-xs border-t">{{ component.name }}</td>
                  <td class="px-6 text-xs border-t">
                    <span>{{ component.quantity }}</span>
                  </td>
                  <td class="px-6 text-right text-xs border-t">{{ $filters.currency(component.unit_price) }}</td>
                  <td class="px-6 text-right text-xs border-t">{{ $filters.currency(component.unit_price * component.quantity) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="calculation.type === 'fve'" class="block -mb-8 -mr-6 p-8 w-full">
        <div class="mt-6 w-full">
          <h2 class="mb-6 text-2xl font-bold">Kalkulace</h2>
        </div>
        <div class="flex">
          <div class="mb-4 ml-auto mr-8 w-full">
            <div class="flex p-4 font-bold bg-gray-100">
              <div class="">Celkem bez DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.price) }}</div>
            </div>
            <div v-if="calculation.data.calculation.salesmanDiscount != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Sleva individuální (bez DPH)</div>
              <div class="ml-auto text-right">
                <span class="text-red-500">- {{ $filters.currency(calculation.data.calculation.salesmanDiscount) }}</span> <br />
                <span class="text-xs">{{ $filters.currency(calculation.data.calculation.price - calculation.data.calculation.salesmanDiscount) }}</span>
              </div>
            </div>
            <div v-if="calculation.data.calculation.wallboxDiscount != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Sleva na fotovoltaiku (bez DPH)</div>
              <div class="ml-auto text-right">
                <span class="text-red-500">- {{ $filters.currency(calculation.data.calculation.wallboxDiscount) }}</span> <br />
                <span class="text-xs">{{ $filters.currency(calculation.data.calculation.price - calculation.data.calculation.salesmanDiscount - calculation.data.calculation.wallboxDiscount) }}</span>
              </div>
            </div>
            <div v-if="calculation.data.calculation.discountedPrice != calculation.data.calculation.price" class="flex p-4 font-bold bg-gray-100">
              <div class="">Celkem vč. slev bez DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.discountedPrice) }}</div>
            </div>

            <div class="flex mt-4 p-4 font-bold bg-gray-100">
              <div class="">Celkem s DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.priceWithTax) }}</div>
            </div>
            <div class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.tax) }}</div>
            </div>

            <div v-if="calculation.data.calculation.mainSubsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Dotace fotovoltaika</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.mainSubsidy) }}</div>
            </div>
            <div v-if="wallBoxSubsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Dotace ekomobilita</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.wallBoxSubsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.waterHeatingSubsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Dotace ohřev vody</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.waterHeatingSubsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.insulationSubsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Dotace zateplení</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.insulationSubsidy) }}</div>
            </div>
            <template v-if="calculation.data.calculation.insulationSubsidy != 0 && Object.keys(calculation.data.calculation.insulationSubsidyParts).length">
              <div v-for="(value, key) in calculation.data.calculation.insulationSubsidyParts" :key="key" class="flex p-4 pl-6 py-1 text-xs bg-gray-200">
                <div class="">{{ key }}</div>
                <div class="ml-auto">{{ $filters.currency(value) }}</div>
              </div>
            </template>
            <div v-if="calculation.data.calculation.subsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="font-bold">Dotace celkem</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.subsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.subsidy != 0" class="flex mt-4 p-4 font-bold bg-green-200">
              <div>Celkem s DPH a dotací</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.totalPrice) }}</div>
            </div>

            <div v-if="calculation.data.calculation.companySubsidy" class="flex mt-4 p-4 font-bold bg-green-200">
              <div class="">Předběžná výše dotace bez DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.companySubsidy.companyPrelimSubsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.companySubsidy && calculation.data.calculation.companySubsidy.companyAdministrativeSubsidy != 0" class="flex p-4 py-2 text-sm bg-green-100">
              <div class="">5% z výše dotace - vyřízení administrace FVE (bez DPH)</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.companySubsidy.companyAdministrativeSubsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.companySubsidy && calculation.data.calculation.companySubsidy.companyEnergyAssesment != 0" class="flex p-4 py-2 text-sm bg-green-100">
              <div class="">Vyřízení a vypracování energetického posudku (bez DPH)</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.companySubsidy.companyEnergyAssesment) }}</div>
            </div>
            <div v-if="calculation.data.calculation.companySubsidy && calculation.data.calculation.companySubsidy.companyProcessingSubsidy != 0" class="flex p-4 py-2 text-sm bg-green-100">
              <div class="">Podání a vyřízení žádosti o dotace (bez DPH)</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.companySubsidy.companyProcessingSubsidy) }}</div>
            </div>
            <div v-if="calculation.data.calculation.totalPrice" class="flex p-4 font-bold bg-green-100">
              <div class="">Předpokládaná cena FVE po odečtení dotace a s ní spojenými náklady (bez DPH)</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.totalPrice) }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="calculation.type === 'heating-water'" class="block -mb-8 -mr-6 p-8 w-full">
        <div class="mt-6 w-full">
          <h2 class="mb-6 text-2xl font-bold">Kalkulace</h2>
        </div>
        <div class="flex">
          <div class="mb-4 ml-auto mr-8 w-full">
            <div class="flex p-4 font-bold bg-gray-100">
              <div class="">Celkem bez DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.price) }}</div>
            </div>

            <div class="flex mt-4 p-4 font-bold bg-gray-100">
              <div class="">Celkem s DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.priceWithTax) }}</div>
            </div>
            <div class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">DPH</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.tax) }}</div>
            </div>
            <div v-if="subsidy != 0" class="flex p-4 py-2 text-sm bg-gray-100">
              <div class="">Dotace</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.subsidy) }}</div>
            </div>
            <div v-if="subsidy != 0" class="flex mt-4 p-4 font-bold bg-green-200">
              <div>Celkem s DPH a dotací</div>
              <div class="ml-auto">{{ $filters.currency(calculation.data.calculation.totalPrice) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link, usePage } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
// import TextAreaInput from '@/Shared/TextAreaInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Icon from '@/Shared/Icon'
import axios from 'axios'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    Icon,
    // TextAreaInput,
  },
  layout: Layout,
  props: {
    sets: Array,
    calculation: Object,
  },
  remember: 'form',
  data() {
    return {}
  },
  methods: {},
}
</script>
